import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const VideoPromotion = () => {
    // --- State ---
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // --- Modal State ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [videoUrlInput, setVideoUrlInput] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- Hooks & Context ---
    // Added userType to determine role
    const { appId, language, userId, userType } = useAppId();
    const lang = language || 'en';

    // --- Role Verification Logic ---
    const activeRole = userType || localStorage.getItem('userType') || localStorage.getItem('designation') || localStorage.getItem('role');
    const roleCode = activeRole ? activeRole.toUpperCase() : 'A';
    const isSubAdmin = roleCode === 'SA' || roleCode === 'SUB_ADMIN';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Video Promotion",
            subtitle: "View and share promotional videos",
            uploadedBy: "Uploaded :",
            loadingData: "Loading Videos...",
            noData: "No promotional videos found.",
            btnAdd: "Add Video",
            btnDelete: "Delete",
            btnChange: "Change Video",
            modalTitle: "Add YouTube Video",
            inputPlaceholder: "Paste YouTube Video Link (URL)...",
            btnCancel: "Cancel",
            btnSubmit: "Add Video",
            adding: "Adding..."
        },
        hi: {
            pageTitle: "वीडियो प्रचार",
            subtitle: "प्रचार वीडियो देखें और साझा करें",
            uploadedBy: "अपलोड :",
            loadingData: "वीडियो लोड हो रहे हैं...",
            noData: "कोई प्रचार वीडियो नहीं मिला।",
            btnAdd: "वीडियो जोड़ें",
            btnDelete: "हटाएं",
            btnChange: "वीडियो बदलें",
            modalTitle: "यूट्यूब वीडियो जोड़ें",
            inputPlaceholder: "यूट्यूब वीडियो लिंक पेस्ट करें...",
            btnCancel: "रद्द करें",
            btnSubmit: "वीडियो जोड़ें",
            adding: "जोड़ रहा है..."
        },
        gu: {
            pageTitle: "વિડિઓ પ્રમોશન",
            subtitle: "પ્રમોશનલ વિડિઓઝ જુઓ અને શેર કરો",
            uploadedBy: "અપલોડ :",
            loadingData: "વિડિઓઝ લોડ થઈ રહ્યા છે...",
            noData: "કોઈ પ્રમોશનલ વિડિઓઝ મળ્યા નથી.",
            btnAdd: "વિડિઓ ઉમેરો",
            btnDelete: "કાઢી નાખો",
            btnChange: "વિડિઓ બદલો",
            modalTitle: "YouTube વિડિઓ ઉમેરો",
            inputPlaceholder: "YouTube વિડિઓ લિંક પેસ્ટ કરો...",
            btnCancel: "રદ કરો",
            btnSubmit: "વિડિઓ ઉમેરો",
            adding: "ઉમેરી રહ્યા છે..."
        }
    };

    const txt = t[lang];

    // --- API Fetch Logic ---
    const fetchVideos = async () => {
        if (!appId) return;

        setLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_prachar_master`,
                { 
                    app_id: appId,
                    prachar_type: "video"
                }
            );

            if (response.data.success && response.data.data && Array.isArray(response.data.data.result)) {
                setVideos(response.data.data.result);
            } else {
                setVideos([]);
            }
        } catch (error) {
            console.error("Error fetching video promotions:", error);
            setVideos([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVideos();
    }, [appId]);

    // --- Global Add Action (Modal Logic) ---
    const handleOpenModal = () => {
        setIsModalOpen(true);
        setVideoUrlInput(""); // Clear previous input
    };

    const handleCloseModal = () => {
        if (!isSubmitting) {
            setIsModalOpen(false);
            setVideoUrlInput("");
        }
    };

    // Change Video logic just re-opens the modal to paste a new link
    const handleChangeVideo = () => {
        setIsModalOpen(true);
        if (videos.length > 0) {
            setVideoUrlInput(videos[0].content);
        } else {
            setVideoUrlInput("");
        }
    };

    const handleSubmitVideo = async () => {
        if (!videoUrlInput || videoUrlInput.trim() === "") {
            alert("Please paste a valid link.");
            return;
        }

        try {
            setIsSubmitting(true);
            const payload = {
                app_id: appId?.toString(),
                prachar_type: "video",
                base64: "", // Empty because we are sending a link, not a file
                content: videoUrlInput.trim(), // The YouTube link
                create_by: userId?.toString()
            };

            // If a video already exists and we are replacing it
            if (videos.length > 0) {
                payload.id = videos[0].id.toString();
            }

            console.log("Uploading Video Payload:", payload);

            const response = await axios.post(
                `${BASE_URL}/api/ins_prachar_master`,
                payload
            );

            if (response.data.success) {
                await fetchVideos(); // Instantly reload to show the new video
                handleCloseModal();  // Close modal on success
            } else {
                alert("Failed to add the video. Server responded but success was false.");
            }
        } catch (error) {
            console.error("Error adding video:", error.response ? error.response.data : error.message);
            alert(`An error occurred while adding the video: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- Individual Card Delete Action ---
    const handleDeleteVideo = async (id) => {
        if (window.confirm("Are you sure you want to delete this video?")) {
            try {
                const payload = {
                    app_id: appId?.toString(),
                    id: id?.toString(),
                    delete_by: userId?.toString()
                };

                const response = await axios.post(
                    `${BASE_URL}/api/del_prachar_master`,
                    payload
                );

                if (response.data.success) {
                    fetchVideos();
                } else {
                    alert("Failed to delete the video. Server responded but success was false.");
                }
            } catch (error) {
                console.error("Error deleting video:", error.response ? error.response.data : error.message);
                alert(`An error occurred while deleting the video: ${error.message}`);
            }
        }
    };

    // --- Loading State ---
    if (loading && videos.length === 0 && !isModalOpen) {
        return (
            <div className="app-container p-4">
                <Header title={txt.pageTitle}/>
                <div className="loader-wrapper">
                    <div className="spinner-modern"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
                <style jsx>{`
                    .app-container { background: #E5E7EB; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
                    .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                    .spinner-modern { width: 50px; height: 50px; border: 4px solid #cbd5e1; border-top: 4px solid #ef4444; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
                    .loader-text { color: #64748b; font-weight: 600; font-size: 1.1rem; }
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                `}</style>
            </div>
        );
    }

    return (
        <div className='app-container p-4'>

            {/* Header */}
            <Header title={txt.pageTitle} />

            {/* Page Action Bar (Subtitle + Add Button) */}
            <div className="page-action-bar mt-3 mb-4">
                <div className="subtitle-container m-0">
                    <p className="subtitle-text">{txt.subtitle}</p>
                </div>
                
                <div className="global-buttons">
                    {/* CONDITIONAL RENDERING FOR BUTTONS - Hidden for Sub Admins */}
                    {!isSubAdmin && (
                        videos.length === 0 ? (
                            <button 
                                className="global-btn btn-add" 
                                onClick={handleOpenModal}
                            >
                                <svg fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                                {txt.btnAdd}
                            </button>
                        ) : (
                            <>
                                <button 
                                    className="global-btn btn-edit" 
                                    onClick={handleChangeVideo}
                                >
                                    <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                        <path d="M12 20h9"></path>
                                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                    </svg>
                                    {txt.btnChange}
                                </button>
                                
                                <button 
                                    className="global-btn btn-delete" 
                                    onClick={() => handleDeleteVideo(videos[0].id)}
                                >
                                    <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                        <polyline points="3 6 5 6 21 6"></polyline>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                        <line x1="10" y1="11" x2="10" y2="17"></line>
                                        <line x1="14" y1="11" x2="14" y2="17"></line>
                                    </svg>
                                    {txt.btnDelete}
                                </button>
                            </>
                        )
                    )}
                </div>
            </div>

            {/* No Data State */}
            {videos.length === 0 && !loading && (
                <div className="row">
                    <div className="col-12 text-center text-muted p-5 empty-state">
                        <div className="empty-icon">🎬</div>
                        <p>{txt.noData}</p>
                    </div>
                </div>
            )}

            {/* --- VIDEO GRID --- */}
            <div className="video-grid pb-5">
                {videos.map((video) => (
                    <div className="video-card" key={video.id}>
                        {/* Video Container */}
                        <div className="media-wrapper">
                            <video 
                                src={video.content} 
                                className="promo-media"
                                controls
                                preload="metadata"
                            >
                                Your browser does not support the video tag.
                            </video>
                        </div>

                        {/* Uploader Details Container */}
                        <div className="info-wrapper">
                            <div className="upload-info">
                                <span className="upload-label">{txt.uploadedBy}</span>
                                <span className="upload-name">{video.name || "Unknown"}</span>
                            </div>
                            <div className="designation-badge">
                                {video.designation || "User"}
                            </div>

                            {/* Individual Delete Button inside Card - Hidden for Sub Admins */}
                            {!isSubAdmin && (
                                <>
                                    <div className="action-divider"></div>
                                    <button 
                                        className="card-delete-btn" 
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevents click from bubbling up if card becomes clickable
                                            handleDeleteVideo(video.id);
                                        }}
                                    >
                                        <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                            <polyline points="3 6 5 6 21 6"></polyline>
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                            <line x1="10" y1="11" x2="10" y2="17"></line>
                                            <line x1="14" y1="11" x2="14" y2="17"></line>
                                        </svg>
                                        {txt.btnDelete}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* --- CUSTOM MODAL UI --- */}
            {isModalOpen && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-box" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">{txt.modalTitle}</h3>
                            <button className="modal-close-btn" onClick={handleCloseModal} disabled={isSubmitting}>
                                &times;
                            </button>
                        </div>
                        
                        <div className="modal-body">
                            <input 
                                type="text" 
                                className="modal-input" 
                                placeholder={txt.inputPlaceholder}
                                value={videoUrlInput}
                                onChange={(e) => setVideoUrlInput(e.target.value)}
                                disabled={isSubmitting}
                                autoFocus
                            />
                        </div>

                        <div className="modal-footer">
                            <button 
                                className="modal-btn btn-secondary" 
                                onClick={handleCloseModal}
                                disabled={isSubmitting}
                            >
                                {txt.btnCancel}
                            </button>
                            <button 
                                className="modal-btn btn-primary" 
                                onClick={handleSubmitVideo}
                                disabled={isSubmitting || !videoUrlInput.trim()}
                            >
                                {isSubmitting ? txt.adding : txt.btnSubmit}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .app-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }

                /* --- PAGE ACTION BAR --- */
                .page-action-bar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .subtitle-text {
                    font-size: 15px;
                    color: #475569;
                    font-weight: 600;
                    margin: 0;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .global-buttons {
                    display: flex;
                    gap: 12px;
                }

                .global-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 16px;
                    border-radius: 10px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: none;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }

                .global-btn svg {
                    width: 16px;
                    height: 16px;
                }

                /* Add Button Global */
                .btn-add {
                    background: #ef4444; /* Standard Red for Video */
                    color: #ffffff;
                    border: 1px solid #dc2626;
                    box-shadow: 0 4px 6px -1px rgba(239, 68, 68, 0.2);
                }
                .btn-add:hover {
                    background: #dc2626;
                    border-color: #b91c1c;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 8px -1px rgba(239, 68, 68, 0.3);
                }
                .btn-add:active {
                    background: #b91c1c;
                    transform: translateY(0);
                }

                /* --- MODAL UI CSS --- */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100vh;
                    background: rgba(15, 23, 42, 0.6);
                    backdrop-filter: blur(4px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    animation: fadeIn 0.2s ease-out;
                }

                .modal-box {
                    background: #ffffff;
                    width: 90%;
                    max-width: 450px;
                    border-radius: 16px;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                    overflow: hidden;
                    animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px 24px;
                    border-bottom: 1px solid #f1f5f9;
                }

                .modal-title {
                    margin: 0;
                    font-size: 1.1rem;
                    font-weight: 700;
                    color: #0f172a;
                }

                .modal-close-btn {
                    background: transparent;
                    border: none;
                    font-size: 1.5rem;
                    line-height: 1;
                    color: #94a3b8;
                    cursor: pointer;
                    transition: color 0.2s ease;
                }

                .modal-close-btn:hover {
                    color: #0f172a;
                }

                .modal-body {
                    padding: 24px;
                }

                .modal-input {
                    width: 100%;
                    padding: 12px 16px;
                    font-size: 15px;
                    color: #334155;
                    background: #f8fafc;
                    border: 1px solid #cbd5e1;
                    border-radius: 10px;
                    outline: none;
                    transition: all 0.2s ease;
                }

                .modal-input:focus {
                    background: #ffffff;
                    border-color: #ef4444;
                    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
                }

                .modal-footer {
                    padding: 16px 24px;
                    background: #f8fafc;
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                    border-top: 1px solid #f1f5f9;
                }

                .modal-btn {
                    padding: 10px 20px;
                    font-size: 14px;
                    font-weight: 600;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .modal-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .btn-secondary {
                    background: #ffffff;
                    border: 1px solid #cbd5e1;
                    color: #475569;
                }

                .btn-secondary:hover:not(:disabled) {
                    background: #f1f5f9;
                    color: #0f172a;
                }

                .btn-primary {
                    background: #ef4444;
                    border: 1px solid #dc2626;
                    color: #ffffff;
                }

                .btn-primary:hover:not(:disabled) {
                    background: #dc2626;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                /* Edit Button Global */
                .btn-edit {
                    background: #ffffff;
                    color: #334155;
                    border: 1px solid #cbd5e1;
                }
                .btn-edit:hover {
                    background: #f8fafc;
                    color: #0f172a;
                    border-color: #94a3b8;
                }
                .btn-edit:active {
                    background: #f1f5f9;
                }

                /* Delete Button Global */
                .btn-delete {
                    background: #fef2f2;
                    color: #ef4444;
                    border: 1px solid #fecaca;
                }
                .btn-delete:hover {
                    background: #fee2e2;
                    color: #dc2626;
                    border-color: #fca5a5;
                }
                .btn-delete:active {
                    background: #fecaca;
                }

                /* --- VIDEO GRID --- */
                .video-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 24px;
                }

                /* --- PREMIUM CARD STYLES --- */
                .video-card {
                    background: #ffffff;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    border: 1px solid #f1f5f9;
                    display: flex;
                    flex-direction: column;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .video-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 15px 30px -5px rgba(0, 0, 0, 0.1);
                    border-color: #e2e8f0;
                }

                /* Media Styling */
                .media-wrapper {
                    width: 100%;
                    height: 220px;
                    background: #0f172a; /* Dark background better for video letterboxing */
                    overflow: hidden;
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .promo-media {
                    width: 100%;
                    height: 100%;
                    object-fit: contain; /* Keeps whole video visible without cropping */
                    outline: none;
                }

                /* Bottom Info Area */
                .info-wrapper {
                    padding: 16px 20px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    background: #ffffff;
                }

                .upload-info {
                    display: flex;
                    align-items: baseline;
                    gap: 6px;
                    flex-wrap: wrap;
                }

                .upload-label {
                    font-size: 13px;
                    color: #64748b;
                    font-weight: 500;
                }

                .upload-name {
                    font-size: 15px;
                    color: #0f172a;
                    font-weight: 700;
                }

                /* Designation Badge */
                .designation-badge {
                    align-self: flex-start;
                    font-size: 11px;
                    font-weight: 700;
                    color: #ef4444; /* Match Video Red Theme */
                    background: #fef2f2;
                    padding: 4px 10px;
                    border-radius: 20px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    border: 1px solid #fecaca;
                }

                /* --- CARD DELETE BUTTON --- */
                .action-divider {
                    height: 1px;
                    background: #f1f5f9;
                    margin: 8px 0 4px 0;
                }

                .card-delete-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    width: 100%;
                    padding: 8px;
                    border-radius: 8px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: none;
                    background: #fef2f2;
                    color: #ef4444;
                }

                .card-delete-btn:hover {
                    background: #fee2e2;
                    color: #dc2626;
                }

                .card-delete-btn:active {
                    background: #fecaca;
                }

                .card-delete-btn svg {
                    width: 16px;
                    height: 16px;
                }

                /* Empty State */
                .empty-state { 
                    background: white; 
                    border-radius: 20px; 
                    border: 2px dashed #cbd5e1; 
                }
                .empty-icon { 
                    font-size: 3rem; 
                    margin-bottom: 10px; 
                    opacity: 0.5; 
                }

                /* Mobile Optimization */
                @media (max-width: 640px) {
                    .page-action-bar {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                    }
                    .global-buttons {
                        width: 100%;
                        display: flex;
                    }
                    .global-btn {
                        flex: 1;
                        justify-content: center;
                    }
                    .video-grid { 
                        grid-template-columns: 1fr; 
                        gap: 16px;
                    }
                    .media-wrapper {
                        height: 250px;
                    }
                }
            `}</style>
        </div>
    );
};

export default VideoPromotion;